import { supabase } from './config';

// Portfolio Profile
export const getProfile = async () => {
  try {
    const { data, error } = await supabase
      .from('portfolio')
      .select('*')
      .eq('id', 'profile')
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = not found
      throw error;
    }
    return data || null;
  } catch (error) {
    console.error('Error getting profile:', error);
    throw error;
  }
};

export const updateProfile = async (profileData) => {
  try {
    const { error } = await supabase
      .from('portfolio')
      .upsert({ id: 'profile', ...profileData }, { onConflict: 'id' });
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

// Helper functions to convert between camelCase (JS) and snake_case (DB)
const toCamelCase = (obj) => {
  if (!obj) return obj;
  const converted = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
    converted[camelKey] = obj[key];
  }
  return converted;
};

const toSnakeCase = (obj) => {
  if (!obj) return obj;
  const converted = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    converted[snakeKey] = obj[key];
  }
  return converted;
};

// Projects
export const getProjects = async () => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('order', { ascending: true });

    if (error) throw error;
    // Convert snake_case to camelCase for components
    return (data || []).map(toCamelCase);
  } catch (error) {
    console.error('Error getting projects:', error);
    throw error;
  }
};

export const addProject = async (projectData) => {
  try {
    // Convert camelCase to snake_case for database
    const dbData = toSnakeCase({
      ...projectData,
      order: Date.now()
    });
    
    const { data, error } = await supabase
      .from('projects')
      .insert(dbData)
      .select()
      .single();

    if (error) throw error;
    return data.id;
  } catch (error) {
    console.error('Error adding project:', error);
    throw error;
  }
};

export const updateProject = async (projectId, projectData) => {
  try {
    // Convert camelCase to snake_case for database
    const dbData = toSnakeCase(projectData);
    
    const { error } = await supabase
      .from('projects')
      .update(dbData)
      .eq('id', projectId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

export const deleteProject = async (projectId) => {
  try {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

// Image Upload
export const uploadImage = async (file, path) => {
  try {
    const { data, error } = await supabase.storage
      .from('portfolio-images')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('portfolio-images')
      .getPublicUrl(data.path);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

export const deleteImage = async (path) => {
  try {
    const { error } = await supabase.storage
      .from('portfolio-images')
      .remove([path]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};

// ============================================
// Categories
// ============================================
export const getCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('order', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting categories:', error);
    throw error;
  }
};

export const addCategory = async (categoryData) => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .insert(categoryData)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding category:', error);
    throw error;
  }
};

export const updateCategory = async (categoryId, categoryData) => {
  try {
    const { error } = await supabase
      .from('categories')
      .update(categoryData)
      .eq('id', categoryId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', categoryId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// ============================================
// Skills
// ============================================
export const getSkills = async () => {
  try {
    const { data, error } = await supabase
      .from('skills')
      .select('*')
      .order('order', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting skills:', error);
    throw error;
  }
};

export const addSkill = async (skillData) => {
  try {
    const { data, error } = await supabase
      .from('skills')
      .insert(skillData)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding skill:', error);
    throw error;
  }
};

export const updateSkill = async (skillId, skillData) => {
  try {
    const { error } = await supabase
      .from('skills')
      .update(skillData)
      .eq('id', skillId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating skill:', error);
    throw error;
  }
};

export const deleteSkill = async (skillId) => {
  try {
    const { error } = await supabase
      .from('skills')
      .delete()
      .eq('id', skillId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting skill:', error);
    throw error;
  }
};

// ============================================
// Experience (Work, Education, Certifications)
// ============================================
export const getExperience = async () => {
  try {
    const { data, error } = await supabase
      .from('experience')
      .select('*')
      .order('order', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting experience:', error);
    throw error;
  }
};

export const addExperience = async (experienceData) => {
  try {
    const { data, error } = await supabase
      .from('experience')
      .insert(experienceData)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding experience:', error);
    throw error;
  }
};

export const updateExperience = async (experienceId, experienceData) => {
  try {
    const { error } = await supabase
      .from('experience')
      .update(experienceData)
      .eq('id', experienceId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating experience:', error);
    throw error;
  }
};

export const deleteExperience = async (experienceId) => {
  try {
    const { error } = await supabase
      .from('experience')
      .delete()
      .eq('id', experienceId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting experience:', error);
    throw error;
  }
};

// ============================================
// Testimonials
// ============================================
export const getTestimonials = async () => {
  try {
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .order('order', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting testimonials:', error);
    throw error;
  }
};

export const addTestimonial = async (testimonialData) => {
  try {
    const { data, error } = await supabase
      .from('testimonials')
      .insert(testimonialData)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding testimonial:', error);
    throw error;
  }
};

export const updateTestimonial = async (testimonialId, testimonialData) => {
  try {
    const { error } = await supabase
      .from('testimonials')
      .update(testimonialData)
      .eq('id', testimonialId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating testimonial:', error);
    throw error;
  }
};

export const deleteTestimonial = async (testimonialId) => {
  try {
    const { error } = await supabase
      .from('testimonials')
      .delete()
      .eq('id', testimonialId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting testimonial:', error);
    throw error;
  }
};

// ============================================
// Enhanced Project Functions (with categories)
// ============================================
export const getProjectsWithCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        category:categories(*)
      `)
      .order('order', { ascending: true });

    if (error) throw error;
    // Convert snake_case to camelCase for components
    return (data || []).map(toCamelCase);
  } catch (error) {
    console.error('Error getting projects with categories:', error);
    throw error;
  }
};

export const getProjectsByCategory = async (categoryId) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        category:categories(*)
      `)
      .eq('category_id', categoryId)
      .order('order', { ascending: true });

    if (error) throw error;
    // Convert snake_case to camelCase for components
    return (data || []).map(toCamelCase);
  } catch (error) {
    console.error('Error getting projects by category:', error);
    throw error;
  }
};

